import { useCallback, useEffect, useState } from "react";
import {
  createClientViewQueryProfile,
  deactivateClientViewQueryProfile,
  listClientViewQueryProfiles,
  previewClientViewQueryProfile,
  updateClientViewQueryProfile,
} from "../../../api/adminClientViewQueryProfilesApi";
import StatusBadge from "../intelligence/StatusBadge";

const EMPTY_PROFILE_FORM = {
  profileKey: "",
  name: "",
  description: "",
  filterJson: '{\n  "listingType": "both",\n  "availabilityStatus": "AVAILABLE",\n  "includeInactive": false,\n  "keywords": ""\n}',
  sortJson: '{\n  "sort": "recent"\n}',
  fieldAllowlistJson: "[]",
  defaultLimit: 24,
  maxLimit: 100,
  isDefault: false,
  isActive: true,
};

function ClientViewQueryProfilesSection({ clientViewId, canManage }) {
  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_PROFILE_FORM);
  const [previewResult, setPreviewResult] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadProfiles = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const body = await listClientViewQueryProfiles(clientViewId);
      setProfiles(body.profiles || []);
    } catch (error) {
      setProfiles([]);
      setErrorMessage(error.message || "Unable to load query profiles.");
    } finally {
      setIsLoading(false);
    }
  }, [clientViewId]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const parseJsonField = (text, fieldName) => {
    try {
      return JSON.parse(text);
    } catch {
      throw new Error(`${fieldName} must be valid JSON.`);
    }
  };

  const buildPayload = () => ({
    profileKey: form.profileKey.trim(),
    name: form.name.trim(),
    description: form.description.trim() || null,
    filterJson: parseJsonField(form.filterJson, "filter_json"),
    sortJson: parseJsonField(form.sortJson, "sort_json"),
    fieldAllowlistJson: parseJsonField(form.fieldAllowlistJson, "field_allowlist_json"),
    defaultLimit: Number(form.defaultLimit),
    maxLimit: Number(form.maxLimit),
    isDefault: Boolean(form.isDefault),
    isActive: Boolean(form.isActive),
  });

  const openCreate = () => {
    setEditingId("new");
    setForm({ ...EMPTY_PROFILE_FORM });
    setPreviewResult(null);
  };

  const openEdit = (profile) => {
    setEditingId(profile.clientViewQueryProfileId);
    setForm({
      profileKey: profile.profileKey,
      name: profile.name,
      description: profile.description || "",
      filterJson: JSON.stringify(profile.filterJson || {}, null, 2),
      sortJson: JSON.stringify(profile.sortJson || {}, null, 2),
      fieldAllowlistJson: JSON.stringify(profile.fieldAllowlistJson || [], null, 2),
      defaultLimit: profile.defaultLimit,
      maxLimit: profile.maxLimit,
      isDefault: profile.isDefault,
      isActive: profile.isActive,
    });
    setPreviewResult(null);
  };

  const handleSave = async () => {
    if (!canManage) {
      return;
    }

    setIsSaving(true);
    setErrorMessage("");

    try {
      const payload = buildPayload();
      if (editingId === "new") {
        await createClientViewQueryProfile(clientViewId, payload);
      } else {
        await updateClientViewQueryProfile(clientViewId, editingId, payload);
      }
      setEditingId(null);
      await loadProfiles();
    } catch (error) {
      setErrorMessage(error.message || "Unable to save query profile.");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = async (profileId) => {
    const id = profileId || editingId;
    if (!id || id === "new") {
      setErrorMessage("Save the profile before previewing, or select an existing profile.");
      return;
    }

    setErrorMessage("");
    try {
      const body = await previewClientViewQueryProfile(clientViewId, id, {});
      setPreviewResult(body);
    } catch (error) {
      setPreviewResult(null);
      setErrorMessage(error.message || "Preview failed.");
    }
  };

  const handleDeactivate = async (profileId) => {
    if (!canManage || !window.confirm("Deactivate this query profile?")) {
      return;
    }

    try {
      await deactivateClientViewQueryProfile(clientViewId, profileId);
      if (editingId === profileId) {
        setEditingId(null);
      }
      await loadProfiles();
    } catch (error) {
      setErrorMessage(error.message || "Unable to deactivate profile.");
    }
  };

  return (
    <section className="panel-card client-view-query-profiles">
      <div className="client-view-query-profiles__header">
        <div>
          <h2>Query profiles</h2>
          <p className="muted">
            Scoped canonical listing queries for the public runtime. Profiles are live when
            active; publishing the Client View manifest does not change profile rows.
          </p>
        </div>
        {canManage ? (
          <button type="button" className="report-submit-cta" onClick={openCreate}>
            Add profile
          </button>
        ) : null}
      </div>

      {errorMessage ? <p className="admin-data-health-error">{errorMessage}</p> : null}
      {isLoading ? <p className="muted">Loading profiles…</p> : null}

      {!isLoading && profiles.length ? (
        <table className="admin-data-health-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Name</th>
              <th>Default</th>
              <th>Active</th>
              <th>Limits</th>
              {canManage ? <th>Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {profiles.map((profile) => (
              <tr key={profile.clientViewQueryProfileId}>
                <td>{profile.profileKey}</td>
                <td>{profile.name}</td>
                <td>{profile.isDefault ? "Yes" : "—"}</td>
                <td>
                  <StatusBadge
                    label={profile.isActive ? "Active" : "Inactive"}
                    tone={profile.isActive ? "confirmed" : "disputed"}
                  />
                </td>
                <td>
                  {profile.defaultLimit} / max {profile.maxLimit}
                </td>
                {canManage ? (
                  <td className="client-view-query-profiles__row-actions">
                    <button
                      type="button"
                      className="midpoint-share-button"
                      onClick={() => openEdit(profile)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="midpoint-share-button"
                      onClick={() => handlePreview(profile.clientViewQueryProfileId)}
                    >
                      Preview
                    </button>
                    {profile.isActive ? (
                      <button
                        type="button"
                        className="midpoint-share-button"
                        onClick={() => handleDeactivate(profile.clientViewQueryProfileId)}
                      >
                        Deactivate
                      </button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {!isLoading && !profiles.length ? (
        <p className="muted">No query profiles yet.</p>
      ) : null}

      {editingId ? (
        <div className="client-view-query-profiles__form panel-card">
          <h3>{editingId === "new" ? "New query profile" : "Edit query profile"}</h3>
          <div className="client-view-query-profiles__form-grid">
            <label className="admin-users-field">
              <span>Profile key</span>
              <input
                type="text"
                value={form.profileKey}
                onChange={(event) => setForm((f) => ({ ...f, profileKey: event.target.value }))}
                disabled={editingId !== "new"}
              />
            </label>
            <label className="admin-users-field">
              <span>Name</span>
              <input
                type="text"
                value={form.name}
                onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
              />
            </label>
            <label className="admin-users-field client-view-query-profiles__full">
              <span>Description</span>
              <input
                type="text"
                value={form.description}
                onChange={(event) =>
                  setForm((f) => ({ ...f, description: event.target.value }))
                }
              />
            </label>
            <label className="admin-users-field">
              <span>Default limit</span>
              <input
                type="number"
                value={form.defaultLimit}
                onChange={(event) =>
                  setForm((f) => ({ ...f, defaultLimit: event.target.value }))
                }
              />
            </label>
            <label className="admin-users-field">
              <span>Max limit</span>
              <input
                type="number"
                value={form.maxLimit}
                onChange={(event) => setForm((f) => ({ ...f, maxLimit: event.target.value }))}
              />
            </label>
            <label className="admin-users-checkbox">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={(event) =>
                  setForm((f) => ({ ...f, isDefault: event.target.checked }))
                }
              />
              Default profile
            </label>
            <label className="admin-users-checkbox">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) =>
                  setForm((f) => ({ ...f, isActive: event.target.checked }))
                }
              />
              Active
            </label>
            <label className="admin-users-field client-view-query-profiles__full">
              <span>filter_json</span>
              <textarea
                rows={8}
                value={form.filterJson}
                onChange={(event) => setForm((f) => ({ ...f, filterJson: event.target.value }))}
              />
            </label>
            <label className="admin-users-field client-view-query-profiles__full">
              <span>sort_json</span>
              <textarea
                rows={3}
                value={form.sortJson}
                onChange={(event) => setForm((f) => ({ ...f, sortJson: event.target.value }))}
              />
            </label>
            <label className="admin-users-field client-view-query-profiles__full">
              <span>field_allowlist_json (empty = default safe fields)</span>
              <textarea
                rows={3}
                value={form.fieldAllowlistJson}
                onChange={(event) =>
                  setForm((f) => ({ ...f, fieldAllowlistJson: event.target.value }))
                }
              />
            </label>
          </div>
          <div className="client-view-query-profiles__form-actions">
            <button
              type="button"
              className="report-submit-cta"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving…" : "Save profile"}
            </button>
            <button
              type="button"
              className="midpoint-share-button"
              onClick={() => handlePreview()}
            >
              Preview
            </button>
            <button
              type="button"
              className="midpoint-share-button"
              onClick={() => setEditingId(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {previewResult ? (
        <section className="panel-card client-view-query-profiles__preview">
          <h3>Preview: {previewResult.profile?.name}</h3>
          <p>
            <strong>{previewResult.summary?.total ?? 0}</strong> total matches (showing{" "}
            {previewResult.summary?.sampleSize ?? 0})
          </p>
          {previewResult.summary?.warnings?.length ? (
            <ul>
              {previewResult.summary.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : null}
          {previewResult.sampleResults?.length ? (
            <ul className="client-view-query-profiles__sample-list">
              {previewResult.sampleResults.map((listing) => (
                <li key={listing.id}>
                  {listing.title} — {listing.price} {listing.currency}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">No sample listings in this preview.</p>
          )}
        </section>
      ) : null}
    </section>
  );
}

export default ClientViewQueryProfilesSection;
